import { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { supabase } from './lib/supabase'
import './App.css'

const stripePromise = loadStripe('pk_test_51TYRz3F6HKL3Tul6B1yj9cnW5A7wWIEOWYtIIKT0lXkGjDUlqN9SUouHAk3fNiOztYVzO8utqilZFD6fIIKGOlbt00mX0MPARl')
const API_URL = 'https://reflections-backend-production.up.railway.app'

const TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
  '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
]

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function CardSetupForm({ onSuccess, customerInfo, appointmentId }) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [ready, setReady] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!stripe || !elements || !ready) return
    setProcessing(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: customerInfo.email,
          name: customerInfo.name,
          appointmentId,
        }),
      })

      const { clientSecret } = await response.json()

      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: customerInfo.name,
              email: customerInfo.email,
            },
          },
        }
      )

      if (setupError) {
        setError(setupError.message)
        setProcessing(false)
        return
      }

      await fetch(`${API_URL}/save-payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setupIntentId: setupIntent.id,
          appointmentId,
        }),
      })

      onSuccess()
    } catch (err) {
      setError('Something went wrong. Please try again.')
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="card-element-wrapper">
        <CardElement
          onReady={() => setReady(true)}
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1a1a1a',
                '::placeholder': { color: '#999' },
              },
            },
          }}
        />
      </div>
      {error && <p className="error-text">{error}</p>}
      <button
        className="btn-primary"
        type="submit"
        disabled={!stripe || processing || !ready}
        style={{ marginTop: '16px' }}>
        {processing ? 'Saving card...' : 'Save Card & Confirm Booking'}
      </button>
    </form>
  )
}

export default function App() {
  const [step, setStep] = useState(1)
  const [services, setServices] = useState([])
  const [staff, setStaff] = useState([])
  const [selectedService, setSelectedService] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' })
  const [loading, setLoading] = useState(true)
  const [confirmed, setConfirmed] = useState(false)
  const [appointmentId, setAppointmentId] = useState(null)
  const [creatingAppointment, setCreatingAppointment] = useState(false)

  const categories = ['All', 'Hair', 'Color', 'Treatment', 'Special']

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    const [servicesRes, staffRes] = await Promise.all([
      supabase.from('services').select(`
        *,
        service_locations (
          id, location_name, address, available_days,
          start_time, end_time, staff_id,
          staff (id, full_name)
        )
      `).eq('active', true).order('category'),
      supabase.from('staff').select('*').eq('active', true),
    ])
    if (!servicesRes.error) setServices(servicesRes.data)
    if (!staffRes.error) setStaff(staffRes.data)
    setLoading(false)
  }

  function handleServiceSelect(service) {
    setSelectedService(service)
    const locations = service.service_locations || []
    if (locations.length > 1) {
      setStep(2) // Go to location selection
    } else if (locations.length === 1) {
      setSelectedLocation(locations[0])
      setSelectedStaff(locations[0].staff)
      setStep(3) // Skip location, go to date
    } else {
      setStep(3) // No locations, go straight to date
    }
  }

  async function proceedToCardStep() {
    if (!customer.name || !customer.email) {
      alert('Please enter your name and email.')
      return
    }
    setCreatingAppointment(true)

    try {
      let { data: existingProfiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', customer.email)

      let customerId = existingProfiles?.[0]?.id

      if (!customerId) {
        const { data: authData } = await supabase.auth.signUp({
          email: customer.email,
          password: Math.random().toString(36).slice(-8),
          options: { data: { full_name: customer.name } },
        })
        customerId = authData?.user?.id

        if (customerId) {
          await supabase.from('profiles').upsert({
            id: customerId,
            full_name: customer.name,
            email: customer.email,
            phone: customer.phone,
            role: 'customer',
          })
        }
      }

      if (!customerId) {
        alert('Could not create profile. Please try again.')
        setCreatingAppointment(false)
        return
      }

      const staffForAppointment = selectedLocation?.staff || selectedStaff

      const { data: aptData, error } = await supabase
        .from('appointments')
        .insert({
          customer_id: customerId,
          staff_id: staffForAppointment?.id,
          service_id: selectedService.id,
          appointment_date: selectedDate,
          start_time: selectedTime,
          end_time: selectedTime,
          total_price: selectedService.price,
          status: 'confirmed',
          location_id: selectedLocation?.id || null,
          location_name: selectedLocation?.location_name || null,
          location_address: selectedLocation?.address || null,
        })
        .select()
        .single()

      if (error || !aptData) {
        alert('Could not create appointment. Please try again.')
        setCreatingAppointment(false)
        return
      }

      setAppointmentId(aptData.id)
      setStep(6)
    } catch (err) {
      alert('Something went wrong. Please try again.')
    }
    setCreatingAppointment(false)
  }

  async function handleCardSaved() {
    await fetch(`${API_URL}/send-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: customer.email,
        name: customer.name,
        service: selectedService.name,
        staff: (selectedLocation?.staff || selectedStaff)?.full_name,
        date: selectedDate,
        time: selectedTime,
        price: selectedService.price,
      }),
    })
    setConfirmed(true)
  }

  const filteredServices = selectedCategory === 'All'
    ? services
    : services.filter(s => s.category === selectedCategory)

  const today = new Date().toISOString().split('T')[0]

  // Filter staff based on selected location's available days
  function getAvailableTimeSlotsForDate(date) {
    if (!selectedLocation || !date) return TIME_SLOTS
    const dayOfWeek = new Date(date + 'T00:00:00').getDay()
    if (!selectedLocation.available_days.includes(dayOfWeek)) return []
    return TIME_SLOTS
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    )
  }

  if (confirmed) {
    return (
      <div className="confirmed">
        <div className="confirmed-icon">🎉</div>
        <h2>Booking Confirmed!</h2>
        <div className="confirmed-details">
          <div className="confirmed-row">
            <span>Service</span>
            <span>{selectedService.name}</span>
          </div>
          {selectedLocation && (
            <div className="confirmed-row">
              <span>Location</span>
              <span>{selectedLocation.location_name}</span>
            </div>
          )}
          <div className="confirmed-row">
            <span>Stylist</span>
            <span>{(selectedLocation?.staff || selectedStaff)?.full_name}</span>
          </div>
          <div className="confirmed-row">
            <span>Date</span>
            <span>{selectedDate}</span>
          </div>
          <div className="confirmed-row">
            <span>Time</span>
            <span>{selectedTime}</span>
          </div>
          {selectedLocation?.address && (
            <div className="confirmed-row">
              <span>Address</span>
              <span>{selectedLocation.address}</span>
            </div>
          )}
          <div className="confirmed-row">
            <span>Total</span>
            <span>${selectedService.price}</span>
          </div>
        </div>
        <p className="confirmed-sub">
          A confirmation email has been sent to {customer.email}
        </p>
        <p className="confirmed-policy">
          💳 Your card has been saved to hold your appointment. You pay after your service. Cancellations within 24 hours are subject to a 30% fee.
        </p>
        <button
          className="btn-primary"
          onClick={() => {
            setConfirmed(false)
            setStep(1)
            setSelectedService(null)
            setSelectedLocation(null)
            setSelectedStaff(null)
            setSelectedDate('')
            setSelectedTime('')
            setAppointmentId(null)
            setCustomer({ name: '', email: '', phone: '' })
          }}>
          Book Another Appointment
        </button>
      </div>
    )
  }

  const totalSteps = selectedService?.service_locations?.length > 1 ? 6 : 5
  const stepLabels = selectedService?.service_locations?.length > 1
    ? ['Service', 'Location', 'Date & Time', 'Stylist', 'Your Info', 'Confirm']
    : ['Service', 'Date & Time', 'Stylist', 'Your Info', 'Confirm']

  return (
    <div className="app">
      <header className="header">
  <img src="/logo.jpg" alt="Reflections Beauty Lounge" className="header-logo" />
</header>

      <div className="progress">
        {['Service', 'Date & Time', 'Stylist', 'Your Info', 'Confirm'].map((label, i) => (
          <div key={i} className={`progress-step ${step >= i + 1 ? 'active' : ''}`}>
            <div className="progress-dot">{i + 1}</div>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className="content">
        {/* Step 1 - Service */}
        {step === 1 && (
          <div>
            <h2>Choose a service</h2>
            <div className="categories">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`cat-pill ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}>
                  {cat}
                </button>
              ))}
            </div>
            <div className="service-list">
              {filteredServices.map(service => (
                <div
                  key={service.id}
                  className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}
                  onClick={() => handleServiceSelect(service)}>
                  <div className="service-info">
                    <h3>{service.name}</h3>
                    <p>{service.description}</p>
                    <span className="duration">{service.duration} mins</span>
                    {service.service_locations?.length > 0 && (
                      <span className="location-badge">
                        📍 {service.service_locations.length} location{service.service_locations.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="service-price">${service.price}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 - Location (only if multiple locations) */}
        {step === 2 && selectedService?.service_locations?.length > 1 && (
          <div>
            <h2>Choose a location</h2>
            <div className="location-list">
              {selectedService.service_locations.map(loc => (
                <div
                  key={loc.id}
                  className={`location-card ${selectedLocation?.id === loc.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedLocation(loc)
                    setSelectedStaff(loc.staff)
                    setStep(3)
                  }}>
                  <div className="location-info">
                    <h3>{loc.location_name}</h3>
                    {loc.address && <p className="location-address">📍 {loc.address}</p>}
                    <p className="location-stylist">✂️ {loc.staff?.full_name}</p>
                    <p className="location-days">
                      🗓 {loc.available_days.map(d => DAYS[d]).join(', ')}
                    </p>
                    <p className="location-hours">
                      🕐 {loc.start_time} — {loc.end_time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn-back" onClick={() => setStep(1)}>← Back</button>
          </div>
        )}

        {/* Step 3 - Date */}
        {step === 3 && (
          <div>
            <h2>Pick a date & time</h2>
            {selectedLocation && (
              <div className="location-reminder">
                📍 {selectedLocation.location_name} —
                Available: {selectedLocation.available_days.map(d => DAYS[d]).join(', ')}
              </div>
            )}
            <input
              type="date"
              className="date-input"
              min={today}
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              onClick={e => e.target.showPicker && e.target.showPicker()}
            />
            {selectedDate && (
              <>
                {getAvailableTimeSlotsForDate(selectedDate).length === 0 ? (
                  <div className="no-slots">
                    <p>No availability on this day for {selectedLocation?.location_name}.</p>
                    <p>Available days: {selectedLocation?.available_days.map(d => DAYS[d]).join(', ')}</p>
                  </div>
                ) : (
                  <>
                    <h3>Available times</h3>
                    <div className="time-grid">
                      {TIME_SLOTS.map(time => (
                        <button
                          key={time}
                          className={`time-slot ${selectedTime === time ? 'active' : ''}`}
                          onClick={() => setSelectedTime(time)}>
                          {time}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            {selectedDate && selectedTime && (
              <button className="btn-primary" onClick={() => setStep(4)}>
                Continue →
              </button>
            )}
            <button className="btn-back" onClick={() => setStep(selectedService?.service_locations?.length > 1 ? 2 : 1)}>← Back</button>
          </div>
        )}

        {/* Step 4 - Stylist (skip if location pre-selected stylist) */}
        {step === 4 && (
          <div>
            <h2>
              {selectedLocation ? `Your stylist` : 'Choose your stylist'}
            </h2>
            {selectedLocation ? (
              <div className="staff-card selected">
                <div className="staff-avatar">
                  {selectedLocation.staff?.full_name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="staff-info">
                  <h3>{selectedLocation.staff?.full_name}</h3>
                  <p>📍 {selectedLocation.location_name}</p>
                </div>
              </div>
            ) : (
              <div className="staff-list">
                {staff.map(member => (
                  <div
                    key={member.id}
                    className={`staff-card ${selectedStaff?.id === member.id ? 'selected' : ''}`}
                    onClick={() => setSelectedStaff(member)}>
                    <div className="staff-avatar">
                      {member.full_name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="staff-info">
                      <h3>{member.full_name}</h3>
                      <p>{member.bio}</p>
                      <div className="specialties">
                        {member.specialties.map(s => (
                          <span key={s} className="specialty-tag">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              className="btn-primary"
              style={{ marginTop: '16px' }}
              onClick={() => setStep(5)}
              disabled={!selectedStaff && !selectedLocation}>
              Continue →
            </button>
            <button className="btn-back" onClick={() => setStep(3)}>← Back</button>
          </div>
        )}

        {/* Step 5 - Your Info */}
        {step === 5 && (
          <div>
            <h2>Your information</h2>
            <div className="summary-card">
              <div className="summary-row">
                <span>Service</span>
                <span>{selectedService?.name}</span>
              </div>
              {selectedLocation && (
                <div className="summary-row">
                  <span>Location</span>
                  <span>{selectedLocation.location_name}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Stylist</span>
                <span>{(selectedLocation?.staff || selectedStaff)?.full_name}</span>
              </div>
              <div className="summary-row">
                <span>Date</span>
                <span>{selectedDate}</span>
              </div>
              <div className="summary-row">
                <span>Time</span>
                <span>{selectedTime}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>${selectedService?.price}</span>
              </div>
            </div>

            <div className="form">
              <input
                className="form-input"
                placeholder="Full name *"
                value={customer.name}
                onChange={e => setCustomer({ ...customer, name: e.target.value })}
              />
              <input
                className="form-input"
                placeholder="Email address *"
                type="email"
                value={customer.email}
                onChange={e => setCustomer({ ...customer, email: e.target.value })}
              />
              <input
                className="form-input"
                placeholder="Phone number"
                type="tel"
                value={customer.phone}
                onChange={e => setCustomer({ ...customer, phone: e.target.value })}
              />
            </div>

            <button
              className="btn-primary"
              onClick={proceedToCardStep}
              disabled={creatingAppointment}>
              {creatingAppointment ? 'Setting up...' : 'Continue →'}
            </button>
            <button className="btn-back" onClick={() => setStep(4)}>← Back</button>
          </div>
        )}

        {/* Step 6 - Card */}
        {step === 6 && appointmentId && (
          <div>
            <h2>Save a card to hold your appointment</h2>
            <div className="card-notice">
              <p>💳 Your card will <strong>not</strong> be charged today.</p>
              <p>You pay after your service at the salon.</p>
              <p>Cancellations within 24 hours are subject to a 30% fee.</p>
            </div>

            <div className="summary-card" style={{ marginBottom: '20px' }}>
              <div className="summary-row">
                <span>Service</span>
                <span>{selectedService?.name}</span>
              </div>
              {selectedLocation && (
                <div className="summary-row">
                  <span>Location</span>
                  <span>{selectedLocation.location_name}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Date</span>
                <span>{selectedDate}</span>
              </div>
              <div className="summary-row total">
                <span>Due at salon</span>
                <span>${selectedService?.price}</span>
              </div>
            </div>

            <Elements stripe={stripePromise}>
              <CardSetupForm
                customerInfo={customer}
                appointmentId={appointmentId}
                onSuccess={handleCardSaved}
              />
            </Elements>

            <button className="btn-back" onClick={() => setStep(5)}>← Back</button>
          </div>
        )}
      </div>
    </div>
  )
}